/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */
import React from 'react';

import { Button, Input, Modal } from '@mairistem/composants';
import { RegistreTreeView } from '@mairistem/administres-utils';
import { request } from '@mairistem/tools';

import { BASE_URL_ETATCIVIL, TYPE_REGISTRE_BY_TYPE_ACTE } from '@/constants';

import type { Registre } from '../../types/Registre';

import 'rsuite/dist/rsuite.min.css';

interface ModalRegistreTreeViewProps {
  open: boolean;
  onClose: () => void;
  onSelect?: (registre: Partial<Registre>) => void;
  /**
   * Filtre `typeRegistre` de `GET /registres/listeByType` (défaut `'nai,jdn,jap'`
   * : nai = naissance, jdn = jugement déclaratif naissance, jap = jugement
   * adoption plénière — comportement historique inchangé). La reconnaissance
   * utilise `'rec'`.
   */
  typeRegistre?: string;
}

/** Même défaut que `InformationsActe`, depuis la source unique. */
const DEFAULT_TYPE_REGISTRE = TYPE_REGISTRE_BY_TYPE_ACTE.naissance;

const ModalRegistreTreeView = (props: ModalRegistreTreeViewProps) => {
  const {
    open,
    onClose,
    onSelect,
    typeRegistre = DEFAULT_TYPE_REGISTRE,
  } = props;

  const [registres, setRegistres] = React.useState<Registre[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [selectedRegistre, setSelectedRegistre] =
    React.useState<Partial<Registre>>();
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (!open) {
      setSelectedRegistre(undefined);
      setSearch('');
      return;
    }

    setLoading(true);
    // Debounce de 300ms pour éviter les appels API à chaque frappe
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      const params = new URLSearchParams({ typeRegistre });
      if (search) {
        params.set('search', search);
      }
      request
        .get(`${BASE_URL_ETATCIVIL}/registres/listeByType?${params}`)
        .then((response: Registre[]) => {
          setRegistres(response);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [open, typeRegistre, search]);

  const handleSearchChange = React.useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
      setSearch(data.value);
    },
    [],
  );

  const handleValidate = React.useCallback(() => {
    if (!selectedRegistre) {
      return;
    }
    onSelect?.(selectedRegistre);
    setSearch('');
    onClose();
  }, [onClose, onSelect, selectedRegistre]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header content="Choisir un registre" />
      <Modal.Content>
        <Input
          fluid
          debounced={false}
          icon="search"
          placeholder="Rechercher..."
          value={search}
          onChange={handleSearchChange}
          style={{ marginBottom: '1rem' }}
        />
        <RegistreTreeView
          registres={registres}
          onSelect={setSelectedRegistre}
          loading={loading}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button content="Annuler" icon="cancel" basic onClick={onClose} />
        <Button
          content="Valider"
          icon="check"
          positive
          disabled={!selectedRegistre}
          onClick={handleValidate}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalRegistreTreeView;
